import {
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import {
  BookOutlined,
  HourglassEmptyRounded,
  InsertDriveFileOutlined,
  LaunchRounded,
  LocalShippingOutlined,
  LocalShippingRounded,
  MoreVertRounded,
  ScheduleRounded
} from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import clsx from 'clsx';
import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RRLink } from 'react-router-dom';
import { DocumentRevisionDashboard } from '../pages/Project/projectSlice';
import { WorkflowStatusLabel } from '../pages/ProjectDocumentRevision/DocumentRevisionView';
import StatusChip from './StatusChip';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    flexAlignStart: {
      display: 'flex',
      flexWrap: 'wrap'
    },
    flexAlignCenter: {
      display: 'flex',
      alignItems: 'center'
    },
    flexGrow: {
      flexGrow: 1
    },
    documentCard: {
      display: 'flex',
      borderBottom: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(1, 0),
      '&:last-child': {
        borderBottom: 'none'
      }
    },
    statusIcon: {
      margin: theme.spacing(0, 0.5, 0, -0.5)
    },
    item70: {
      width: '70%',
      [theme.breakpoints.down('xs')]: {
        width: '100%'
      }
    },
    item30: {
      width: '30%',
      [theme.breakpoints.down('xs')]: {
        width: '100%',
        textAlign: 'right'
      }
    },
    fullList: {
      width: 'auto'
    },
    listHeader: {
      margin: theme.spacing(-1, 0)
    },
    divider: {
      margin: theme.spacing(1, 0)
    },
    verticalDivider: {
      margin: theme.spacing(0.5, 1)
    },
    actionArea: {
      flexGrow: 1,
      display: 'flex',
      [theme.breakpoints.down('xs')]: {
        display: 'block'
      },
      textDecoration: 'none',
      color: theme.palette.text.primary,
      transition: '0.4s',
      '&:hover, &.Mui-focusVisible': {
        color: theme.palette.primary.main
      }
    },
    chip: {
      margin: theme.spacing(0, 1, 0, -0.5),
      cursor: 'pointer',
      fontWeight: 'normal',
      fontSize: 13
    },
    alertPerson: {
      marginTop: theme.spacing(0.5),
      padding: theme.spacing(0, 1)
    },
    mtb: {
      margin: theme.spacing(0.5, 0)
    },
    ml: {
      marginLeft: theme.spacing(0.5)
    },
    actualIssueDateIcon: {
      color: theme.palette.text.disabled
    },
    successColor: {
      color: theme.palette.success.main
    },
    warningColor: {
      color: theme.palette.warning.main
    },
    errorColor: {
      color: theme.palette.error.main
    }
  })
);

export interface DocumentRevision {
  id: number;
  name: string;
  revision: string;
}

interface DocRevDashboardCardProps {
  renderDocumentRevisions: DocumentRevisionDashboard[];
}

function getClassNameActualIssueDateIcon(
  classes: ReturnType<typeof useStyles>,
  planIssueDate: Date,
  actualIssueDate: Date | null
) {
  const classNames = [classes.actualIssueDateIcon];

  if (actualIssueDate) {
    classNames.push(
      moment(planIssueDate) > moment(actualIssueDate)
        ? classes.successColor
        : classes.warningColor
    );
  } else if (moment(planIssueDate) < moment()) {
    classNames.push(classes.errorColor);
  }

  return classNames.join(' ');
}

export default function DocRevDashboardCard({
  renderDocumentRevisions
}: DocRevDashboardCardProps) {
  const classes = useStyles();
  const { t } = useTranslation();

  const [open, setOpen] = useState<boolean>(false);
  const [selectedDocRevision, setSelectedDocRevision] =
    useState<DocumentRevisionDashboard | null>(null);

  const MENUS = [
    {
      text: t('docRevDashboardCard.openDocument') as string,
      icon: <BookOutlined />,
      to: `/documents/${selectedDocRevision?.projectDocument.id}`,
      disabledText: ''
    },
    {
      text: t('docRevDashboardCard.openDocumentRevision') as string,
      icon: <InsertDriveFileOutlined />,
      to: `/document-revisions/${selectedDocRevision?.id}`,
      disabledText: ''
    },
    {
      text: t('docRevDashboardCard.openCurrentWorkflow') as string,
      icon: <LocalShippingOutlined />,
      to: `/approvals/${selectedDocRevision?.currentProjectDocumentWorkflowId}`,
      disabledText: t('docRevDashboardCard.workflowNotStarted') as string
    }
  ];

  function openDrawer(docRevision: DocumentRevisionDashboard) {
    setOpen(true);
    setSelectedDocRevision(docRevision);
  }

  function closeDrawer() {
    setOpen(false);
  }

  return (
    <div>
      {renderDocumentRevisions.map((documentRevision) => {
        const isCurrentWorkflow = Boolean(
          documentRevision.currentProjectDocumentWorkflow
        );
        return (
          <div className={classes.documentCard} key={documentRevision.id}>
            <div className={classes.flexGrow}>
              <RRLink
                className={classes.actionArea}
                to={
                  isCurrentWorkflow
                    ? `/approvals/${documentRevision.currentProjectDocumentWorkflowId}`
                    : `/document-revisions/${documentRevision.id}`
                }
                target="_blank"
              >
                <div className={clsx(classes.flexAlignStart, classes.flexGrow)}>
                  <div className={classes.item70}>
                    <Typography variant="subtitle2">
                      <Tooltip
                        title={
                          documentRevision.projectDocument.discipline
                            ?.cfihosName ?? ''
                        }
                      >
                        <Chip
                          className={classes.chip}
                          component="span"
                          label={
                            documentRevision.projectDocument.discipline.name
                          }
                          variant="outlined"
                          size="small"
                        />
                      </Tooltip>
                      {documentRevision.projectDocument.name}
                    </Typography>
                    <Typography variant="body2" color="textPrimary">
                      {t('docRevDashboardCard.purpose')}: {documentRevision.purpose?.abbreviation},
                      {t('docRevDashboardCard.revision')}: {documentRevision.revision?.name}
                    </Typography>
                    <div className={clsx(classes.flexAlignCenter, classes.mtb)}>
                      <Tooltip title={t('docRevDashboardCard.tooltipPlanIssueDate') as string}>
                        <div className={classes.flexAlignCenter}>
                          <ScheduleRounded color="action" fontSize="small" />
                          <Typography
                            className={classes.ml}
                            variant="caption"
                            color="textPrimary"
                          >
                            {moment(documentRevision.planIssueDate).format(
                              'll'
                            )}
                          </Typography>
                        </div>
                      </Tooltip>
                      <Divider
                        className={classes.verticalDivider}
                        orientation="vertical"
                        flexItem
                      />
                      <Tooltip title={t('docRevDashboardCard.tooltipActualIssueDate') as string}>
                        <div className={classes.flexAlignCenter}>
                          <LocalShippingRounded
                            className={getClassNameActualIssueDateIcon(
                              classes,
                              documentRevision.planIssueDate,
                              documentRevision.actualIssueDate
                            )}
                            fontSize="small"
                          />
                          <Typography
                            className={classes.ml}
                            variant="caption"
                            color={
                              documentRevision.actualIssueDate
                                ? 'textPrimary'
                                : 'textSecondary'
                            }
                          >
                            {documentRevision.actualIssueDate
                              ? moment(documentRevision.actualIssueDate).format(
                                  'lll'
                                )
                              : t('docRevDashboardCard.notStart')}
                          </Typography>
                        </div>
                      </Tooltip>
                    </div>
                  </div>
                  <div className={classes.item30}>
                    <StatusChip
                      workflowWithStep={
                        documentRevision.currentProjectDocumentWorkflow !== null
                          ? documentRevision.currentProjectDocumentWorkflow
                          : undefined
                      } //TODO: need to revise backend for returning projectDocumentWorkflow with undefined value
                      planIssueDate={documentRevision.planIssueDate}
                      actualIssueDate={documentRevision.actualIssueDate}
                    />
                  </div>
                </div>
              </RRLink>
              {documentRevision.currentProjectDocumentWorkflow &&
                documentRevision.currentProjectDocumentWorkflow
                  .workflowStatusId === WorkflowStatusLabel.InProgress && (
                  <Alert
                    className={classes.alertPerson}
                    icon={<HourglassEmptyRounded fontSize="inherit" />}
                    severity="info"
                  >
                    {documentRevision.currentProjectDocumentWorkflow.currentWorkflowStep.userApprovals
                      .filter(
                        (userApproval) => userApproval.approvalTypeId === null
                      )
                      .map((userApproval) => {
                        if (userApproval.user) {
                          const { firstName, lastName } = userApproval.user;
                          return [firstName, lastName].join(' ');
                        }
                        return t('docRevDashboardCard.removedUser') as string;
                      })
                      .join(', ')}
                  </Alert>
                )}
            </div>
            <div>
              <IconButton
                size="small"
                onClick={() => openDrawer(documentRevision)}
              >
                <MoreVertRounded />
              </IconButton>
            </div>
          </div>
        );
      })}
      <Drawer anchor="bottom" open={open} onClose={closeDrawer}>
        <div className={classes.fullList} role="presentation">
          <List>
            <ListItem className={classes.listHeader}>
              <ListItemText
                primary={`${selectedDocRevision?.projectDocument.name}`}
                secondary={`${t('docRevDashboardCard.purpose')}: ${selectedDocRevision?.purpose?.abbreviation}, ${t('docRevDashboardCard.revision')}:${selectedDocRevision?.revision?.name}`}
              />
            </ListItem>
            <Divider className={classes.divider} />
            {MENUS.map(({ text, icon, to, disabledText }) => {
              const isDisabled =
                text === MENUS[2].text &&
                selectedDocRevision?.currentProjectDocumentWorkflow === null;
              //TODO: need to revise backend for returning projectDocumentWorkflow with undefined value
              return (
                <ListItem
                  button
                  onClick={() => {
                    window.open(`${process.env.PUBLIC_URL}${to}`, '_blank');
                  }}
                  disabled={isDisabled}
                  key={text}
                >
                  <ListItemIcon>{icon}</ListItemIcon>
                  <ListItemText
                    primary={text}
                    secondary={isDisabled && disabledText}
                  />
                  <LaunchRounded color="action" fontSize="small" />
                </ListItem>
              );
            })}
          </List>
        </div>
      </Drawer>
    </div>
  );
}
